import React, { useEffect, useRef, useState } from 'react';
import { FileText } from 'lucide-react';

const PDFJS_VERSION = '4.10.38';
const WORKER_SRC = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;

let pdfjsPromise = null;
function loadPdfJs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/build/pdf.mjs').then((mod) => {
      mod.GlobalWorkerOptions.workerSrc = WORKER_SRC;
      return mod;
    });
  }
  return pdfjsPromise;
}

/**
 * Renders the first page of a PDF as a canvas preview.
 * Falls back to a gradient "PDF" tile when the PDF is unreachable or fails to render.
 * Props:
 *   src:  URL to the PDF (relative or absolute)
 *   className: optional wrapper classes
 */
const PdfThumbnail = ({ src, className = '' }) => {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'error'

  useEffect(() => {
    if (!src) { setStatus('error'); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        const task = pdfjs.getDocument({ url: src, isEvalSupported: false, disableAutoFetch: true });
        const doc = await task.promise;
        if (cancelled) return;
        const page = await doc.getPage(1);
        // Compute scale so the rendered page is ~1200px on the long side (crisp)
        const baseViewport = page.getViewport({ scale: 1 });
        const target = 1200;
        const scale = target / Math.max(baseViewport.width, baseViewport.height);
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport, canvas }).promise;
        if (!cancelled) setStatus('ok');
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [src]);

  if (status === 'error') {
    return (
      <div
        className={`bg-gradient-to-br from-rose-500 via-pink-600 to-rose-700 flex items-center justify-center p-8 ${className}`}
        data-testid="pdf-thumbnail-fallback"
      >
        <div className="text-center text-white">
          <FileText className="w-16 h-16 mx-auto mb-2 drop-shadow-lg" />
          <div className="text-[11px] uppercase tracking-widest font-bold opacity-90">PDF</div>
        </div>
      </div>
    );
  }
  return (
    <div className={`relative bg-gray-100 ${className}`} data-testid="pdf-thumbnail">
      <canvas ref={canvasRef} className="w-full h-full object-cover" />
      {status === 'loading' && (
        <div className="absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center">
          <FileText className="w-10 h-10 text-gray-300" />
        </div>
      )}
    </div>
  );
};

export default PdfThumbnail;
